import React, { useState, useEffect, useMemo } from 'react';
import { FiPlus, FiEye, FiTrash2, FiDownload } from 'react-icons/fi';
import api from '../services/api';
import Modal from '../components/Modal';
import InkooporderBetaalinfoCard from '../components/InkooporderBetaalinfoCard';
import { useSearch } from '../contexts/SearchContext';
import '../components/Button.css';
import './Inkooporders.css';
import { ProductNaamMetKlasse } from '../components/ProductKlasseIcon';

const formatCurrency = (value, valuta = 'EUR') => {
  const num = parseFloat(value || 0);
  const symbol = valuta === 'USD' ? '$' : '€';
  return `${symbol}${num.toFixed(2)}`;
};

const getRegelEffectivePrice = (regel) => {
  // Nooit verkoopprijs tonen: als eenheidsprijs gelijk is aan product_prijs (verkoopprijs), gebruik inkoopprijs of 0
  const ep = parseFloat(regel.product_inkoopprijs ?? regel.eenheidsprijs ?? 0) || 0;
  const verkoop = parseFloat(regel.product_prijs);
  if (regel.product_prijs != null && !isNaN(verkoop) && Math.abs(ep - verkoop) < 0.001) {
    return parseFloat(regel.product_inkoopprijs) || 0;
  }
  return ep;
};

const Inkooporders = () => {
  const [inkooporders, setInkooporders] = useState([]);
  const [leveranciers, setLeveranciers] = useState([]);
  const [producten, setProducten] = useState([]);
  const [loading, setLoading] = useState(true);
  const { searchTerm } = useSearch();
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailEditMode, setDetailEditMode] = useState(false);
  const [detailForm, setDetailForm] = useState(null);
  const [levertijdenInline, setLevertijdenInline] = useState({ verwachte_leverdatum: '', ata_tijd: '' });
  const [levertijdenSaving, setLevertijdenSaving] = useState(false);
  const [formData, setFormData] = useState({
    leverancier_id: '',
    verwachte_leverdatum: '',
    ata_tijd: '',
    opmerkingen: '',
    interne_notitie: '',
    regels: []
  });

  useEffect(() => {
    fetchInkooporders();
    fetchLeveranciers();
    fetchProducten();
  }, []);

  const fetchInkooporders = async () => {
    try {
      const response = await api.get('/inkooporders');
      setInkooporders(response.data);
    } catch (error) {
      console.error('Error fetching inkooporders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeveranciers = async () => {
    try {
      const response = await api.get('/leveranciers');
      setLeveranciers(response.data);
    } catch (error) {
      console.error('Error fetching leveranciers:', error);
    }
  };

  const fetchProducten = async () => {
    try {
      const response = await api.get('/producten');
      setProducten(response.data);
    } catch (error) {
      console.error('Error fetching producten:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.regels.length === 0) {
      alert('Voeg minimaal één product toe aan de order');
      return;
    }

    try {
      await api.post('/inkooporders', { ...formData, status: 'concept' });
      setShowModal(false);
      resetForm();
      fetchInkooporders();
    } catch (error) {
      console.error('Error saving inkooporder:', error);
      alert('Fout bij opslaan: ' + (error.response?.data?.error || error.message));
    }
  };

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    const s = String(dateStr).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    return s.split('T')[0] || '';
  };

  const formatDateTimeLocalForInput = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const h = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${y}-${m}-${day}T${h}:${min}`;
  };

  const handleViewDetail = async (id) => {
    try {
      // Altijd eerst productlijst verversen, zodat ook producten die na het laden
      // van de pagina zijn aangemaakt beschikbaar zijn om aan de order toe te voegen.
      fetchProducten();
      const response = await api.get(`/inkooporders/${id}`);
      setSelectedOrder(response.data);
      setDetailForm({
        leverancier_id: response.data.leverancier_id || '',
        verwachte_leverdatum: formatDateForInput(response.data.verwachte_leverdatum),
        ata_tijd: formatDateTimeLocalForInput(response.data.ata_tijd),
        opmerkingen: response.data.opmerkingen || '',
        interne_notitie: response.data.interne_notitie || '',
        status: response.data.status || 'concept',
        regels: (response.data.regels || []).map(r => {
          const ep = parseFloat(r.eenheidsprijs);
          const verkoop = parseFloat(r.product_prijs);
          const isVerkoopprijs = r.product_prijs != null && !isNaN(verkoop) && Math.abs(ep - verkoop) < 0.001;
          const eenheidsprijs = isVerkoopprijs ? (parseFloat(r.product_inkoopprijs) || 0) : r.eenheidsprijs;
          return { id: r.id, product_id: r.product_id, aantal: r.aantal, eenheidsprijs, cbm_per_stuk: r.cbm_per_stuk, product_inkoopprijs: r.product_inkoopprijs, product_prijs: r.product_prijs };
        })
      });
      setDetailEditMode(false);
      setLevertijdenInline({
        verwachte_leverdatum: formatDateForInput(response.data.verwachte_leverdatum),
        ata_tijd: formatDateTimeLocalForInput(response.data.ata_tijd)
      });
      setShowDetailModal(true);
    } catch (error) {
      console.error('Error fetching order detail:', error);
    }
  };

  const saveLevertijden = async () => {
    if (!selectedOrder) return;
    setLevertijdenSaving(true);
    try {
      await api.put(`/inkooporders/${selectedOrder.id}/levertijden`, {
        verwachte_leverdatum: levertijdenInline.verwachte_leverdatum || null,
        ata_tijd: levertijdenInline.ata_tijd || null
      });
      const response = await api.get(`/inkooporders/${selectedOrder.id}`);
      setSelectedOrder(response.data);
      fetchInkooporders();
    } catch (error) {
      console.error('Error saving levertijden:', error);
      alert('Fout bij opslaan levertijden: ' + (error.response?.data?.error || error.message));
    } finally {
      setLevertijdenSaving(false);
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await api.put(`/inkooporders/${id}/status`, { status });
      fetchInkooporders();
      if (showDetailModal) {
        const response = await api.get(`/inkooporders/${id}`);
        setSelectedOrder(response.data);
        setDetailForm(prev => prev ? { ...prev, status, regels: (response.data.regels || []).map(r => {
          const ep = parseFloat(r.eenheidsprijs);
          const verkoop = parseFloat(r.product_prijs);
          const isVerkoopprijs = r.product_prijs != null && !isNaN(verkoop) && Math.abs(ep - verkoop) < 0.001;
          const eenheidsprijs = isVerkoopprijs ? (parseFloat(r.product_inkoopprijs) || 0) : r.eenheidsprijs;
          return { id: r.id, product_id: r.product_id, aantal: r.aantal, eenheidsprijs, cbm_per_stuk: r.cbm_per_stuk, product_inkoopprijs: r.product_inkoopprijs, product_prijs: r.product_prijs };
        }) } : prev);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Fout bij bijwerken status: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDelete = async (id) => {
    // Eenvoudige bevestiging - verwijder direct
    if (!window.confirm('Order verwijderen?')) {
      return;
    }

    try {
      await api.delete(`/inkooporders/${id}`);
      fetchInkooporders();
    } catch (error) {
      console.error('Error deleting inkooporder:', error);
      alert('Fout bij verwijderen: ' + (error.response?.data?.error || error.message));
    }
  };

  const addRegel = () => {
    setFormData({
      ...formData,
      regels: [...formData.regels, { product_id: '', aantal: 1, eenheidsprijs: 0 }]
    });
  };

  const updateRegel = (index, field, value) => {
    const newRegels = [...formData.regels];
    newRegels[index][field] = field === 'aantal' || field === 'eenheidsprijs' ? parseFloat(value) || 0 : value;
    
    // Auto-fill inkoopprijs from product (geen verkoopprijs)
    if (field === 'product_id') {
      const product = producten.find(p => p.id === parseInt(value));
      if (product) {
        newRegels[index].eenheidsprijs = product.inkoopprijs ?? 0;
      }
    }
    
    setFormData({ ...formData, regels: newRegels });
  };

  const removeRegel = (index) => {
    setFormData({
      ...formData,
      regels: formData.regels.filter((_, i) => i !== index)
    });
  };

  // Detail edit helpers
  const addDetailRegel = () => {
    if (!detailForm) return;
    setDetailForm({
      ...detailForm,
      regels: [...detailForm.regels, { product_id: '', aantal: 1, eenheidsprijs: 0 }]
    });
  };

  const updateDetailRegel = (index, field, value) => {
    if (!detailForm) return;
    const regels = [...detailForm.regels];
    regels[index][field] = field === 'aantal' || field === 'eenheidsprijs' ? parseFloat(value) || 0 : value;

    if (field === 'product_id') {
      const product = producten.find(p => p.id === parseInt(value, 10));
      if (product) {
        regels[index].eenheidsprijs = product.inkoopprijs ?? 0;
        if (product.cbm_per_stuk) regels[index].cbm_per_stuk = product.cbm_per_stuk;
      }
    }

    setDetailForm({ ...detailForm, regels });
  };

  const removeDetailRegel = (index) => {
    if (!detailForm) return;
    setDetailForm({
      ...detailForm,
      regels: detailForm.regels.filter((_, i) => i !== index)
    });
  };

  const saveDetailChanges = async () => {
    if (!selectedOrder || !detailForm) return;

    if (!detailForm.leverancier_id) {
      alert('Selecteer een leverancier');
      return;
    }

    if (!detailForm.regels || detailForm.regels.length === 0) {
      alert('Voeg minimaal één product toe');
      return;
    }

    try {
      await api.put(`/inkooporders/${selectedOrder.id}`, {
        ...detailForm,
        verwachte_leverdatum: detailForm.verwachte_leverdatum || null,
        ata_tijd: detailForm.ata_tijd || null
      });
      await fetchInkooporders();
      await handleViewDetail(selectedOrder.id);
      setDetailEditMode(false);
    } catch (error) {
      console.error('Error updating order:', error);
      alert('Fout bij opslaan: ' + (error.response?.data?.error || error.message));
    }
  };

  const resetForm = () => {
    setFormData({
      leverancier_id: '',
      verwachte_leverdatum: '',
      ata_tijd: '',
      opmerkingen: '',
      interne_notitie: '',
      regels: []
    });
  };

  const getStatusBadge = (status) => {
    const statuses = {
      'concept': { label: 'Nieuw voorstel', class: 'status-concept' },
      'in_productie': { label: 'In productie', class: 'status-in-productie' },
      'onderweg': { label: 'Onderweg', class: 'status-onderweg' },
      'geleverd': { label: 'Geleverd', class: 'status-geleverd' }
    };
    return statuses[status] || { label: status, class: '' };
  };

  const calculateTotal = () => {
    return formData.regels.reduce((sum, regel) => {
      return sum + (regel.aantal * regel.eenheidsprijs);
    }, 0);
  };

  const [companyInfo, setCompanyInfo] = useState({ name: 'Company Name', address: '', email: '', phone: '', website: '' });
  useEffect(() => {
    api.get('/parameters').then((res) => {
      const paramMap = {};
      (res.data || []).forEach((p) => { paramMap[p.parameter_naam] = p.waarde; });
      setCompanyInfo({
        name: paramMap.company_name || 'Company Name',
        address: paramMap.company_address || '',
        email: paramMap.company_email || '',
        phone: paramMap.company_phone || '',
        website: paramMap.company_website || ''
      });
    }).catch(() => {});
  }, []);

  const loadJsPdfLibrary = () => new Promise((resolve, reject) => {
    if (window.jspdf?.jsPDF) { resolve(window.jspdf.jsPDF); return; }
    const el = document.getElementById('jspdf-cdn');
    if (el) { el.addEventListener('load', () => resolve(window.jspdf.jsPDF)); return; }
    const s = document.createElement('script');
    s.id = 'jspdf-cdn';
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
    s.onload = () => resolve(window.jspdf.jsPDF);
    s.onerror = () => reject(new Error('jsPDF load failed'));
    document.body.appendChild(s);
  });

  const fetchLogoDataUrl = async () => {
    try {
      const r = await fetch('/logo.png');
      if (!r.ok) return null;
      const blob = await r.blob();
      return await new Promise((resolve) => { const reader = new FileReader(); reader.onloadend = () => resolve(reader.result); reader.readAsDataURL(blob); });
    } catch { return null; }
  };

  const formatDateNL = (v) => v ? new Date(v).toLocaleDateString('nl-NL') : '-';

  const handleDownloadPdf = async (orderOrId) => {
    if (!orderOrId) return;
    try {
      let order = typeof orderOrId === 'object' ? orderOrId : null;
      const orderId = order?.id || orderOrId;
      if (!order?.regels?.length) {
        const res = await api.get(`/inkooporders/${orderId}`);
        order = res.data;
      }
      const JsPDF = await loadJsPdfLibrary();
      const doc = new JsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      let totalCbm = 0;
      const logoUrl = await fetchLogoDataUrl();
      if (logoUrl) doc.addImage(logoUrl, 'PNG', 14, 12, 45, 45 / 2.5);
      doc.setFontSize(16).text('Purchase Order', pageWidth - 14, 18, { align: 'right' });
      doc.setFontSize(10);
      doc.text(companyInfo.name || 'Company Name', pageWidth - 14, 26, { align: 'right' });
      if (companyInfo.address) doc.text(companyInfo.address, pageWidth - 14, 32, { align: 'right' });
      if (companyInfo.email) doc.text(companyInfo.email, pageWidth - 14, 38, { align: 'right' });
      if (companyInfo.phone) doc.text(companyInfo.phone, pageWidth - 14, 44, { align: 'right' });
      if (companyInfo.website) doc.text(companyInfo.website, pageWidth - 14, 50, { align: 'right' });
      let cursorY = 60;
      doc.setFontSize(12).text('Order Details', 14, cursorY, { underline: true });
      doc.setFontSize(10);
      cursorY += 6;
      doc.text(`Order number: ${order.ordernummer}`, 14, cursorY); cursorY += 5;
      doc.text(`Order date: ${formatDateNL(order.datum)}`, 14, cursorY); cursorY += 5;
      doc.text(`Expected delivery: ${formatDateNL(order.verwachte_leverdatum)}`, 14, cursorY); cursorY += 5;
      doc.text(`ATA time: ${order.ata_tijd ? new Date(order.ata_tijd).toLocaleString('nl-NL') : '-'}`, 14, cursorY); cursorY += 5;
      doc.text(`Status: ${order.status || 'open'}`, 14, cursorY); cursorY += 7;
      if (order.opmerkingen) {
        const notitieLines = doc.splitTextToSize(`Notitie: ${order.opmerkingen}`, pageWidth - 28);
        notitieLines.forEach((line) => { doc.text(line, 14, cursorY); cursorY += 5; });
        cursorY += 2;
      }
      doc.setFontSize(12).text('Supplier', 14, cursorY, { underline: true });
      cursorY += 6;
      doc.setFontSize(10);
      doc.text(order.leverancier_naam || 'Supplier', 14, cursorY); cursorY += 5;
      if (order.leverancier_contactpersoon) { doc.text(`Contact: ${order.leverancier_contactpersoon}`, 14, cursorY); cursorY += 5; }
      if (order.leverancier_email) { doc.text(`Email: ${order.leverancier_email}`, 14, cursorY); cursorY += 5; }
      if (order.leverancier_telefoon) { doc.text(`Phone: ${order.leverancier_telefoon}`, 14, cursorY); cursorY += 5; }
      if (order.leverancier_adres) { doc.text(`Address: ${order.leverancier_adres}`, 14, cursorY); cursorY += 5; }
      cursorY += 6;
      const colProductStart = 14, colProductMaxWidth = 42, colSkuStart = 60, colSkuMaxWidth = 16, colQtyStart = 88, colCbmStart = 110, colUnitPriceStart = 140, colLineTotalStart = 175;
      doc.setFontSize(10);
      doc.text('Product', colProductStart, cursorY); doc.text('SKU/EAN', colSkuStart, cursorY); doc.text('Qty', colQtyStart, cursorY, { align: 'right' }); doc.text('CBM (total)', colCbmStart, cursorY, { align: 'right' }); doc.text('Inkoopprijs', colUnitPriceStart, cursorY, { align: 'right' }); doc.text('Line total', colLineTotalStart, cursorY, { align: 'right' });
      doc.line(14, cursorY + 2, pageWidth - 14, cursorY + 2);
      const orderValuta = order.leverancier_valuta || 'EUR';
      const fmtCurr = (v) => formatCurrency(v, orderValuta);
      cursorY += 8;
      doc.setFontSize(9);
      (order.regels || []).forEach((regel) => {
        if (cursorY > pageHeight - 30) { doc.addPage(); cursorY = 20; }
        const effPrice = getRegelEffectivePrice(regel);
        const lineTotal = (regel.aantal || 0) * effPrice;
        const cbmPer = regel.cbm_per_stuk || regel.cbm || 0;
        const lineCbm = (regel.aantal || 0) * cbmPer;
        totalCbm += lineCbm;
        const productName = regel.product_naam || 'Product';
        const productLines = doc.splitTextToSize(productName, colProductMaxWidth);
        const startY = cursorY;
        productLines.forEach((line, i) => { doc.text(line, colProductStart, cursorY); if (i < productLines.length - 1) cursorY += 4; });
        const skuText = regel.sku || regel.ean || '-';
        const skuLines = doc.splitTextToSize(skuText, colSkuMaxWidth);
        skuLines.forEach((line, i) => doc.text(line, colSkuStart, startY + (i * 4)));
        doc.text(String(regel.aantal || 0), colQtyStart, startY, { align: 'right' });
        doc.text(lineCbm ? lineCbm.toFixed(3) : '-', colCbmStart, startY, { align: 'right' });
        doc.text(fmtCurr(effPrice), colUnitPriceStart, startY, { align: 'right' });
        doc.text(fmtCurr(lineTotal), colLineTotalStart, startY, { align: 'right' });
        cursorY = Math.max(cursorY, startY + (Math.max(productLines.length, skuLines.length) * 4) + 2);
      });
      cursorY += 6;
      if (totalCbm > 0) { doc.setFontSize(11).text(`Total CBM: ${totalCbm.toFixed(3)}`, pageWidth - 14, cursorY, { align: 'right' }); cursorY += 6; }
      const pdfTotal = (order.regels || []).reduce((s, r) => s + ((r.aantal || 0) * getRegelEffectivePrice(r)), 0);
      doc.setFontSize(12).text(`Total: ${fmtCurr(pdfTotal)}`, pageWidth - 14, cursorY, { align: 'right' });
      doc.save(`purchase-order-${order.ordernummer || order.id}.pdf`);
    } catch (err) {
      console.error('Error generating PDF:', err);
      alert('Kon PDF niet genereren. Probeer het opnieuw.');
    }
  };

  const productById = useMemo(() => {
    const map = {};
    (producten || []).forEach((p) => {
      if (p?.id) map[p.id] = p;
    });
    return map;
  }, [producten]);

  const productBySku = useMemo(() => {
    const map = {};
    (producten || []).forEach((p) => {
      if (p?.sku) map[p.sku] = p;
    });
    return map;
  }, [producten]);

  // Filter + sorteer inkooporders
  const filteredInkooporders = useMemo(() => {
    const list = (!searchTerm || !searchTerm.trim())
      ? inkooporders
      : inkooporders.filter(order => {
          const searchLower = searchTerm.toLowerCase().trim();
          const ordernummer = String(order.ordernummer || '').toLowerCase();
          const leverancierNaam = String(order.leverancier_naam || '').toLowerCase();
          const opmerkingen = String(order.opmerkingen || '').toLowerCase();
          const interneNotitie = String(order.interne_notitie || '').toLowerCase();
          return ordernummer.includes(searchLower) ||
                 leverancierNaam.includes(searchLower) ||
                 opmerkingen.includes(searchLower) ||
                 interneNotitie.includes(searchLower);
        });

    const statusRank = {
      concept: 1,
      in_productie: 2,
      onderweg: 3,
      geleverd: 4
    };

    return [...list].sort((a, b) => {
      const ra = statusRank[a.status] || 99;
      const rb = statusRank[b.status] || 99;
      if (ra !== rb) return ra - rb;
      // secundair: datum desc
      const da = a.datum ? new Date(a.datum).getTime() : 0;
      const db = b.datum ? new Date(b.datum).getTime() : 0;
      return db - da;
    });
  }, [inkooporders, searchTerm]);

  if (loading) {
    return <div className="loading">Laden...</div>;
  }

  return (
    <div className="inkooporders">
      <div className="page-header">
        <h1 className="page-title">Inkooporders</h1>
        <button className="btn btn-primary" onClick={() => { resetForm(); fetchProducten(); setShowModal(true); }}>
          <FiPlus /> Nieuwe Order
        </button>
      </div>

      {searchTerm && (
        <div className="search-results-info" style={{ marginBottom: '1rem', padding: '0.75rem', background: '#f0fffe', borderRadius: '8px', border: '1px solid #3FDFCF' }}>
          <strong>{filteredInkooporders.length}</strong> van <strong>{inkooporders.length}</strong> order(s) gevonden voor "{searchTerm}"
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Ordernummer</th>
              <th>Status wijzigen</th>
              <th style={{ width: '50px' }}></th>
              <th>Leverancier</th>
              <th>Datum</th>
              <th>Verwachte Leverdatum</th>
              <th>ATA Tijd</th>
              <th>Totaal</th>
              <th>Status</th>
              <th>Acties</th>
            </tr>
          </thead>
          <tbody>
            {filteredInkooporders.length === 0 ? (
              <tr>
                <td colSpan="10" className="empty-state">
                  {searchTerm ? `Geen inkooporders gevonden voor "${searchTerm}"` : 'Geen inkooporders gevonden'}
                </td>
              </tr>
            ) : (
              filteredInkooporders.map((order) => {
                const status = getStatusBadge(order.status);
                return (
                  <tr key={order.id} className={order.status === 'geleverd' ? 'delivered-row' : ''}>
                    <td><strong>{order.ordernummer}</strong></td>
                    <td>
                      <select
                        value={order.status}
                        onChange={(e) => handleStatusChange(order.id, e.target.value)}
                        className="status-select"
                        title="Wijzig status"
                      >
                        <option value="concept">Nieuw voorstel</option>
                        <option value="in_productie">In productie</option>
                        <option value="onderweg">Onderweg</option>
                        <option value="geleverd">Geleverd</option>
                      </select>
                    </td>
                    <td>
                      <button 
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(order.id);
                        }} 
                        title="Verwijderen"
                        style={{ 
                          background: 'none',
                          border: 'none',
                          padding: '0.5rem',
                          cursor: 'pointer',
                          color: '#ff6b6b',
                          fontSize: '1.2rem',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <FiTrash2 />
                      </button>
                    </td>
                    <td>{order.leverancier_naam || '-'}</td>
                    <td>{order.datum ? new Date(order.datum).toLocaleDateString('nl-NL') : '-'}</td>
                    <td>{order.verwachte_leverdatum ? new Date(order.verwachte_leverdatum).toLocaleDateString('nl-NL') : '-'}</td>
                    <td>{order.ata_tijd ? new Date(order.ata_tijd).toLocaleString('nl-NL') : '-'}</td>
                    <td>{formatCurrency(order.totaal_bedrag, order.leverancier_valuta)}</td>
                    <td>
                      <span className={`status-badge ${status.class}`}>{status.label}</span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn-icon" onClick={() => handleViewDetail(order.id)} title="Bekijken">
                          <FiEye />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onClose={() => { setShowModal(false); resetForm(); }} title="Nieuwe Inkooporder">
        <form onSubmit={handleSubmit} className="form">
          <div className="form-group">
            <label>Leverancier *</label>
            <select
              value={formData.leverancier_id}
              onChange={(e) => setFormData({ ...formData, leverancier_id: e.target.value })}
              required
            >
              <option value="">Selecteer leverancier</option>
              {leveranciers.map((lev) => (
                <option key={lev.id} value={lev.id}>{lev.naam}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Verwachte Leverdatum</label>
            <input
              type="date"
              value={formData.verwachte_leverdatum}
              onChange={(e) => setFormData({ ...formData, verwachte_leverdatum: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label>ATA Tijd (Verwachte Aankomsttijd)</label>
            <input
              type="datetime-local"
              value={formData.ata_tijd}
              onChange={(e) => setFormData({ ...formData, ata_tijd: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label>Notitie voor leverancier</label>
            <textarea
              value={formData.opmerkingen}
              onChange={(e) => setFormData({ ...formData, opmerkingen: e.target.value })}
              placeholder="Komt op de inkooporder PDF"
              rows="2"
            />
          </div>
          <div className="form-group">
            <label>Interne notitie</label>
            <textarea
              value={formData.interne_notitie}
              onChange={(e) => setFormData({ ...formData, interne_notitie: e.target.value })}
              placeholder="Alleen intern zichtbaar, niet op PDF"
              rows="2"
            />
          </div>

          <div className="order-regels">
            <div className="regels-header">
              <h3>Producten</h3>
              <button type="button" className="btn btn-secondary btn-sm" onClick={addRegel}>
                <FiPlus /> Product Toevoegen
              </button>
            </div>

            {formData.regels.map((regel, index) => (
              <div key={index} className="regel-row">
                <select
                  value={regel.product_id}
                  onChange={(e) => updateRegel(index, 'product_id', e.target.value)}
                  required
                >
                  <option value="">Selecteer product</option>
                  {producten.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.naam} (€{parseFloat(product.inkoopprijs ?? 0).toFixed(2)})
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  min="1"
                  value={regel.aantal}
                  onChange={(e) => updateRegel(index, 'aantal', e.target.value)}
                  placeholder="Aantal"
                  required
                />
                <input
                  type="number"
                  step="0.01"
                  value={regel.eenheidsprijs}
                  onChange={(e) => updateRegel(index, 'eenheidsprijs', e.target.value)}
                  placeholder="Inkoopprijs"
                  required
                />
                <span className="regel-totaal">
                  {formatCurrency(regel.aantal * regel.eenheidsprijs, leveranciers.find(l => String(l.id) === String(formData.leverancier_id))?.valuta || 'EUR')}
                </span>
                <button type="button" className="btn-icon btn-danger" onClick={() => removeRegel(index)}>
                  <FiTrash2 />
                </button>
              </div>
            ))}

            {formData.regels.length > 0 && (
              <div className="order-totaal">
                <strong>Totaal: {formatCurrency(calculateTotal(), leveranciers.find(l => String(l.id) === String(formData.leverancier_id))?.valuta || 'EUR')}</strong>
              </div>
            )}
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); resetForm(); }}>
              Annuleren
            </button>
            <button type="submit" className="btn btn-primary">
              Order Aanmaken
            </button>
          </div>
        </form>
      </Modal>

      {/* Detail Modal */}
      <Modal show={showDetailModal} onClose={() => setShowDetailModal(false)} title={`Order ${selectedOrder?.ordernummer}`}>
        {selectedOrder && (
          <div className="order-detail">
            <div className="detail-section">
              <div className="order-detail-info-layout">
                <div className="order-detail-info-main">
                  <h3>Order Informatie</h3>
                  {!detailEditMode ? (
                <>
                  <div className="status-change-container">
                    <label className="status-change-label">Wijzig status:</label>
                    <select
                      value={selectedOrder.status}
                      onChange={(e) => handleStatusChange(selectedOrder.id, e.target.value)}
                      className="status-select status-select-prominent"
                    >
                      <option value="concept">Nieuw voorstel</option>
                      <option value="in_productie">In productie</option>
                      <option value="onderweg">Onderweg</option>
                      <option value="geleverd">Geleverd</option>
                    </select>
                  </div>
                  <p><strong>Leverancier:</strong> {selectedOrder.leverancier_naam}</p>
                  <p><strong>Datum:</strong> {selectedOrder.datum ? new Date(selectedOrder.datum).toLocaleDateString('nl-NL') : '-'}</p>
                  <div className="levertijden-inline" style={{ marginTop: '0.75rem' }}>
                    <div style={{ marginBottom: '0.5rem' }}><strong>Levertijden</strong></div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'flex-end' }}>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: '0.85rem' }}>Verwachte levertijd</label>
                        <input
                          type="date"
                          value={levertijdenInline.verwachte_leverdatum}
                          onChange={(e) => setLevertijdenInline(prev => ({ ...prev, verwachte_leverdatum: e.target.value }))}
                          style={{ padding: '0.4rem 0.5rem' }}
                        />
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: '0.85rem' }}>ATA tijd</label>
                        <input
                          type="datetime-local"
                          value={levertijdenInline.ata_tijd}
                          onChange={(e) => setLevertijdenInline(prev => ({ ...prev, ata_tijd: e.target.value }))}
                          style={{ padding: '0.4rem 0.5rem' }}
                        />
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={saveLevertijden}
                        disabled={levertijdenSaving}
                      >
                        {levertijdenSaving ? 'Opslaan…' : 'Levertijden opslaan'}
                      </button>
                    </div>
                  </div>
                  <p><strong>Status:</strong> <span className={`status-badge ${getStatusBadge(selectedOrder.status).class}`}>{getStatusBadge(selectedOrder.status).label}</span></p>
                  {selectedOrder.opmerkingen && <p><strong>Notitie voor leverancier:</strong> {selectedOrder.opmerkingen}</p>}
                  {selectedOrder.interne_notitie && <p><strong>Interne notitie:</strong> {selectedOrder.interne_notitie}</p>}
                </>
              ) : (
                <>
                  <div className="form-group">
                    <label>Leverancier *</label>
                    <select
                      value={detailForm?.leverancier_id || ''}
                      onChange={(e) => setDetailForm({ ...detailForm, leverancier_id: e.target.value })}
                    >
                      <option value="">Selecteer leverancier</option>
                      {leveranciers.map((lev) => (
                        <option key={lev.id} value={lev.id}>{lev.naam}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem' }}>Levertijden</h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                      <div className="form-group" style={{ flex: '1 1 200px', marginBottom: 0 }}>
                        <label>Verwachte levertijd</label>
                        <input
                          type="date"
                          value={detailForm?.verwachte_leverdatum || ''}
                          onChange={(e) => setDetailForm({ ...detailForm, verwachte_leverdatum: e.target.value })}
                        />
                      </div>
                      <div className="form-group" style={{ flex: '1 1 200px', marginBottom: 0 }}>
                        <label>ATA tijd (verwachte aankomst)</label>
                        <input
                          type="datetime-local"
                          value={detailForm?.ata_tijd || ''}
                          onChange={(e) => setDetailForm({ ...detailForm, ata_tijd: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Status</label>
                    <select
                      value={detailForm?.status || 'concept'}
                      onChange={(e) => setDetailForm({ ...detailForm, status: e.target.value })}
                    >
                      <option value="concept">Nieuw voorstel</option>
                      <option value="in_productie">In productie</option>
                      <option value="onderweg">Onderweg</option>
                      <option value="geleverd">Geleverd</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Notitie voor leverancier</label>
                    <textarea
                      rows="2"
                      value={detailForm?.opmerkingen || ''}
                      onChange={(e) => setDetailForm({ ...detailForm, opmerkingen: e.target.value })}
                      placeholder="Komt op de inkooporder PDF"
                    />
                  </div>
                  <div className="form-group">
                    <label>Interne notitie</label>
                    <textarea
                      rows="2"
                      value={detailForm?.interne_notitie || ''}
                      onChange={(e) => setDetailForm({ ...detailForm, interne_notitie: e.target.value })}
                      placeholder="Alleen intern zichtbaar, niet op PDF"
                    />
                  </div>
                </>
              )}
                </div>
                <div className="order-detail-aside">
                  <InkooporderBetaalinfoCard
                    order={selectedOrder}
                    onOrderRefresh={(data) => {
                      setSelectedOrder(data);
                      fetchInkooporders();
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ width: '100%' }}
                    onClick={() => handleDownloadPdf(selectedOrder)}
                    title="Download inkooporder als PDF"
                  >
                    <FiDownload /> Download PDF
                  </button>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h3>Producten</h3>
              {!detailEditMode ? (
                <table className="detail-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>SKU</th>
                      <th>Aantal</th>
                      <th>Inkoopprijs</th>
                      <th>CBM per stuk</th>
                      <th>CBM totaal</th>
                      <th>Totaal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedOrder.regels?.map((regel, index) => {
                      const product = productById[regel.product_id] || productBySku[regel.sku];
                      const cbmPerStuk = regel.cbm_per_stuk || (product?.cbm_per_stuk ? parseFloat(product.cbm_per_stuk) : 0);
                      const cbmTotaal = (regel.aantal || 0) * cbmPerStuk;
                      const effPrice = getRegelEffectivePrice(regel);
                      const lineTotal = (regel.aantal || 0) * effPrice;
                      const valuta = selectedOrder.leverancier_valuta || 'EUR';
                      return (
                        <tr key={index}>
                          <td><ProductNaamMetKlasse productId={regel.product_id} naam={regel.product_naam} /></td>
                          <td>{regel.sku || '-'}</td>
                          <td>{regel.aantal}</td>
                          <td>{formatCurrency(effPrice, valuta)}</td>
                          <td>{cbmPerStuk > 0 ? cbmPerStuk.toFixed(3) : '-'}</td>
                          <td>{cbmTotaal > 0 ? cbmTotaal.toFixed(3) : '-'}</td>
                          <td>{formatCurrency(lineTotal, valuta)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="4"><strong>Totaal</strong></td>
                      <td></td>
                      <td><strong>
                        {selectedOrder.regels?.reduce((sum, regel) => {
                          const product = productById[regel.product_id] || productBySku[regel.sku];
                          const cbmPerStuk = regel.cbm_per_stuk || (product?.cbm_per_stuk ? parseFloat(product.cbm_per_stuk) : 0);
                          return sum + ((regel.aantal || 0) * cbmPerStuk);
                        }, 0).toFixed(3)}
                      </strong></td>
                      <td><strong>{formatCurrency(selectedOrder.regels?.reduce((s, r) => s + ((r.aantal || 0) * getRegelEffectivePrice(r)), 0), selectedOrder.leverancier_valuta)}</strong></td>
                    </tr>
                  </tfoot>
                </table>
              ) : (
                <>
                  {detailForm?.regels?.map((regel, index) => {
                    const product = productById[regel.product_id];
                    const cbmPerStuk = regel.cbm_per_stuk || (product?.cbm_per_stuk ? parseFloat(product.cbm_per_stuk) : 0);
                    const cbmTotaal = (regel.aantal || 0) * cbmPerStuk;
                    return (
                      <div key={index} className="regel-row">
                        <select
                          value={regel.product_id}
                          onChange={(e) => updateDetailRegel(index, 'product_id', e.target.value)}
                          required
                        >
                          <option value="">Selecteer product</option>
                          {producten.map((product) => (
                            <option key={product.id} value={product.id}>
                              {product.naam} (€{parseFloat(product.inkoopprijs ?? 0).toFixed(2)})
                            </option>
                          ))}
                        </select>
                        <input
                          type="number"
                          min="1"
                          value={regel.aantal}
                          onChange={(e) => updateDetailRegel(index, 'aantal', e.target.value)}
                          placeholder="Aantal"
                          required
                        />
                        <input
                          type="number"
                          step="0.01"
                          value={regel.eenheidsprijs}
                          onChange={(e) => updateDetailRegel(index, 'eenheidsprijs', e.target.value)}
                          placeholder="Inkoopprijs"
                          required
                        />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.85rem', color: '#666' }}>
                          <span style={{ fontWeight: '600', color: '#3FDFCF' }}>
                            CBM: {cbmPerStuk > 0 ? cbmPerStuk.toFixed(3) : '-'}
                          </span>
                          <span style={{ fontSize: '0.8rem' }}>
                            Totaal: {cbmTotaal > 0 ? cbmTotaal.toFixed(3) : '-'}
                          </span>
                        </div>
                        <span className="regel-totaal">{formatCurrency(regel.aantal * regel.eenheidsprijs, selectedOrder?.leverancier_valuta || 'EUR')}</span>
                        <button type="button" className="btn-icon btn-danger" onClick={() => removeDetailRegel(index)}>
                          <FiTrash2 />
                        </button>
                      </div>
                    );
                  })}
                  <button type="button" className="btn btn-secondary btn-sm" onClick={addDetailRegel} style={{ marginTop: '0.75rem' }}>
                    <FiPlus /> Regel toevoegen
                  </button>
                  {detailForm?.regels?.length > 0 && (
                    <div className="order-totaal" style={{ marginTop: '0.75rem' }}>
                      <strong>
                        Totaal CBM: {detailForm.regels.reduce((sum, regel) => {
                          const product = productById[regel.product_id];
                          const cbmPerStuk = regel.cbm_per_stuk || (product?.cbm_per_stuk ? parseFloat(product.cbm_per_stuk) : 0);
                          return sum + ((regel.aantal || 0) * cbmPerStuk);
                        }, 0).toFixed(3)}
                      </strong>
                      <br />
                      <strong>
                        Totaal: {formatCurrency(detailForm.regels.reduce((sum, r) => sum + (r.aantal * r.eenheidsprijs), 0), selectedOrder?.leverancier_valuta || 'EUR')}
                      </strong>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="detail-actions" style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
              {!detailEditMode ? (
                <button className="btn btn-primary" onClick={() => { fetchProducten(); setDetailEditMode(true); }}>
                  Bewerk order
                </button>
              ) : (
                <>
                  <button className="btn btn-secondary" onClick={() => { setDetailEditMode(false); setDetailForm(null); handleViewDetail(selectedOrder.id); }}>
                    Annuleren
                  </button>
                  <button className="btn btn-primary" onClick={saveDetailChanges}>
                    Opslaan
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Inkooporders;
